import React, { useState, useEffect, useCallback } from "react";
import cn from "classnames";
import styles from "./ContractDatesDerogations.module.sass";
import Card from "../../../components/Card";
import TextInput from "../../../components/TextInput";
import DualButton from "../../../components/DualButton";
import Dropdown from "../../../components/Dropdown";



const DEROGATION_TYPES = [
  { value: "11", title: "11 - Age de l'apprenti inférieur à 16 ans" },
  { value: "12", title: "12 - Age supérieur à 29 ans, cas spécifiques prévus dans le code du travail" },
  { value: "21", title: "21 - Réduction de la durée du contrat ou de la période d'apprentissage" },
  { value: "22", title: "22 - Allongement de la durée du contrat ou de la période d'apprentissage" },
  { value: "50", title: "50 - Cumule de dérogations" },
  { value: "60", title: "60 - Autre dérogation" }
];


const ContractDatesDerogations = ({ className, data, updateData, currentStep }) => {
  // Récupérer les données du contrat et du folder depuis les props
  const contractDatesInfo = data?.contract?.datesDerogations || {};
  const folderData = data?.folder || {};

  // Fonction pour convertir les dates PocketBase (ISO) vers le format HTML date (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', dateString, error);
      return '';
    }
  };

  // États locaux pour les champs
  const [derogationType, setDerogationType] = useState(() => {
    // Trouver l'option correspondant à la value stockée en BDD
    const storedValue = contractDatesInfo.derogation_type || '';
    return DEROGATION_TYPES.find(option => option.value === storedValue) || DEROGATION_TYPES[0];
  });
  const [hasDerogation, setHasDerogation] = useState(contractDatesInfo.has_derogation || 'Non');

  const [workHours, setWorkHours] = useState(contractDatesInfo.work_hours || '');
  const [workMinutes, setWorkMinutes] = useState(contractDatesInfo.work_minutes || '');
  const [startDateOfContract, setStartDateOfContract] = useState(contractDatesInfo.start_date_of_contract || '');
  const [endDateOfContract, setEndDateOfContract] = useState(contractDatesInfo.end_date_of_contract || '');
  const [trainingStartDate, setTrainingStartDate] = useState(
    contractDatesInfo.training_start_date || formatDateForInput(folderData.training_start_date) || ''
  );
  const [trainingEndDate, setTrainingEndDate] = useState(
    contractDatesInfo.training_end_date || formatDateForInput(folderData.training_end_date) || ''
  );

  // État pour suivre la validité du formulaire
  const [isFormValid, setIsFormValid] = useState(false);

  // Fonction de validation du formulaire
  const validateForm = useCallback(() => {
    // Champs obligatoires : heures, minutes, dates de début/fin contrat
    const isWorkHoursValid = workHours && workHours.trim().length > 0;
    const isWorkMinutesValid = workMinutes && workMinutes.trim().length > 0;
    const isStartDateValid = startDateOfContract && startDateOfContract.trim().length > 0;
    const isEndDateValid = endDateOfContract && endDateOfContract.trim().length > 0;

    // Dérogation : obligatoire seulement si "Oui" est sélectionné
    const isDerogationValid = hasDerogation === 'Non' || 
      (hasDerogation === 'Oui' && derogationType && derogationType.value);

    const formValid = isWorkHoursValid && isWorkMinutesValid && 
                     isStartDateValid && isEndDateValid && isDerogationValid;

    setIsFormValid(formValid);

    if (updateData) {
      updateData('isValid', formValid);
    }

    return formValid;
  }, [workHours, workMinutes, startDateOfContract, endDateOfContract, hasDerogation, derogationType, updateData]);

  // Effet pour valider le formulaire à chaque changement
  useEffect(() => {
    validateForm();
  }, [validateForm]);

  // Effet pour initialiser les dates de formation depuis les données du folder
  useEffect(() => {
    if (folderData.training_start_date && !contractDatesInfo.training_start_date) {
      const formattedStartDate = formatDateForInput(folderData.training_start_date);
      setTrainingStartDate(formattedStartDate);
      handleInputChange('training_start_date', formattedStartDate);
    }
    if (folderData.training_end_date && !contractDatesInfo.training_end_date) {
      const formattedEndDate = formatDateForInput(folderData.training_end_date);
      setTrainingEndDate(formattedEndDate);
      handleInputChange('training_end_date', formattedEndDate);
    }
  }, [folderData]);

  // Effet pour initialiser les données du contrat lorsqu'elles changent
  useEffect(() => {
    if (data?.contract?.datesDerogations) {
      const contractInfo = data.contract.datesDerogations;
      setWorkHours(contractInfo.work_hours || '');
      setWorkMinutes(contractInfo.work_minutes || '');
      setStartDateOfContract(contractInfo.start_date_of_contract || '');
      setEndDateOfContract(contractInfo.end_date_of_contract || '');
      setTrainingStartDate(contractInfo.training_start_date || formatDateForInput(folderData.training_start_date) || '');
      setTrainingEndDate(contractInfo.training_end_date || formatDateForInput(folderData.training_end_date) || '');
      setHasDerogation(contractInfo.has_derogation || 'Non');
      
      // Mettre à jour le type de dérogation avec la value correspondante
      const storedValue = contractInfo.derogation_type || '';
      const selectedOption = DEROGATION_TYPES.find(option => option.value === storedValue) || DEROGATION_TYPES[0];
      setDerogationType(selectedOption);
    }
  }, [data?.contract?.datesDerogations, folderData]);

  // Effet pour mettre à jour les dates quand folderData change
  useEffect(() => {
    if (folderData.training_start_date) {
      const formattedStartDate = formatDateForInput(folderData.training_start_date);
      setTrainingStartDate(formattedStartDate);
    }
    if (folderData.training_end_date) {
      const formattedEndDate = formatDateForInput(folderData.training_end_date);
      setTrainingEndDate(formattedEndDate);
    }
  }, [folderData]);

  // Gestionnaire pour les changements de données
  const handleInputChange = (name, value) => {
    if (updateData) {
      updateData('datesDerogations', { ...data.contract?.datesDerogations, [name]: value });
    }
  };

  // Gestionnaire pour le DualButton
  const handleDualButtonChange = (name, value) => {
    if (updateData) {
      updateData('datesDerogations', { ...data.contract?.datesDerogations, [name]: value });
    }
  };

  return (
    <>
      <Card className={cn(styles.card, className)} title="Dates et durée de travail" classTitle="title-green">

        <div className={styles.subtitle}>Durée hebdomadaire de travail</div>
        <div className={styles.group}>
          <TextInput
            className={styles.field}
            label="Heures"
            name="work_hours"
            type="number"
            placeholder="Ex: 35"
            value={workHours}
            onChange={(e) => {
              setWorkHours(e.target.value);
              handleInputChange('work_hours', e.target.value);
            }}
            required
          />
          <TextInput
            className={styles.field}
            label="Minutes"
            name="work_minutes"
            type="number"
            placeholder="Ex: 30"
            value={workMinutes}
            onChange={(e) => {
              setWorkMinutes(e.target.value);
              handleInputChange('work_minutes', e.target.value);
            }}
            required
          />
        </div>

        <div className={styles.subtitle}>Dates de contrat</div>
        <div className={styles.group}>
          <TextInput
            className={styles.field}
            label="Date de début de contrat"
            name="start_date_of_contract"
            type="date"
            value={startDateOfContract}
            onChange={(e) => {
              setStartDateOfContract(e.target.value);
              handleInputChange('start_date_of_contract', e.target.value);
            }}
            required
          />
          <TextInput
            className={styles.field}
            label="Date de fin de contrat"
            name="end_date_of_contract"
            type="date"
            value={endDateOfContract}
            onChange={(e) => {
              setEndDateOfContract(e.target.value);
              handleInputChange('end_date_of_contract', e.target.value);
            }}
            required
          />
        </div>

        <div className={styles.subtitle}>Dates de formation choisies par l'école</div>
        <div className={styles.group}>
          <TextInput
            className={styles.field}
            label="Date de début de la formation"
            name="training_start_date"
            type="date"
            value={trainingStartDate}
            onChange={(e) => {
              setTrainingStartDate(e.target.value);
              handleInputChange('training_start_date', e.target.value);
            }}
            disabled={true}
            required
          />
          <TextInput
            className={styles.field}
            label="Date de fin de la formation"
            name="training_end_date"
            type="date"
            value={trainingEndDate}
            onChange={(e) => {
              setTrainingEndDate(e.target.value);
              handleInputChange('training_end_date', e.target.value);
            }}
            disabled={true}
            required
          />
        </div>
      </Card>
      <Card className={cn(styles.card, className)} title="Dérogation" classTitle="title-blue">

        <div className={styles.field}>
          <DualButton
            label="Demande de dérogation"
            option1="Oui"
            option2="Non"
            value={hasDerogation}
            onChange={(value) => {
              setHasDerogation(value);
              handleDualButtonChange('has_derogation', value);
            }}
            multiSelect={false}
          />
        </div>

        {hasDerogation === 'Oui' && (
          <Dropdown
            className={styles.field}
            label="Type de dérogation"
            name="derogation-type"
            options={DEROGATION_TYPES}
            setValue={(selectedOption) => {
              setDerogationType(selectedOption);
              handleInputChange('derogation_type', selectedOption.value);
            }}
            value={derogationType}
            required
          />
        )}
      </Card>
    </>
  );
};

export default ContractDatesDerogations;
